/**
 * タブ切り替え機能
 * 
 * .tabクラスを持つタブリストに対してタブ切り替え機能を提供します。
 * data-tab属性を持つタブとdata-panel属性を持つパネルを関連付けます。
 * パネルが未読み込みの場合は、AJAXでデータを取得します。
 */

/**
 * AJAXで学校リストを取得
 * @param {string} parentTabId - 親タブID（例: tab-language）
 * @param {string} regionTabId - 地域タブID（例: region-auckland）
 * @returns {Promise<string>} HTML文字列
 */
async function fetchSchoolList(parentTabId, regionTabId) {
  if (!window.originAjax) {
    throw new Error('originAjaxオブジェクトが定義されていません。');
  }

  const formData = new FormData();
  formData.append('action', 'get_school_list');
  formData.append('nonce', window.originAjax.nonce);
  formData.append('parent_tab_id', parentTabId);
  formData.append('region_tab_id', regionTabId);

  try {
    const response = await fetch(window.originAjax.ajaxUrl, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.data?.message || 'データの取得に失敗しました。');
    }

    return data.data.html;
  } catch (error) {
    console.error('学校リストの取得エラー:', error);
    return '<ul class="schoolList"><li>データの読み込みに失敗しました。</li></ul>';
  }
}

/**
 * タブ切り替え機能を初期化
 */
export function setupTabs() {
  const tabLists = document.querySelectorAll('.tab');
  
  if (tabLists.length === 0) {
    return;
  }

  // 初期化時：学校リストの未読み込みパネルを空にする（パフォーマンス向上のため）
  const allPanels = document.querySelectorAll('.panel[data-panel]');
  allPanels.forEach((panel) => {
    const panelId = panel.getAttribute('data-panel');
    const isSchoolListPanel = panelId && panelId.startsWith('region-');
    
    if (isSchoolListPanel) {
      const isLoaded = panel.getAttribute('data-loaded') === 'true';
      
      if (!isLoaded) {
        // 未読み込みのパネルは空にする
        panel.innerHTML = '';
        panel.setAttribute('data-loaded', 'false');
      }
    }
  });

  // 初期表示時に表示されているパネル（showクラスがある）を自動的に読み込む
  // 地域タブがあるパネル（region-で始まる）と、地域タブがないパネル（大学・小学校）の両方に対応
  const visiblePanels = document.querySelectorAll('.panel.show[data-loaded="false"]');
  visiblePanels.forEach(async (panel) => {
    const panelId = panel.getAttribute('data-panel');
    const isLoaded = panel.getAttribute('data-loaded') === 'true';
    
    if (!isLoaded) {
      // 親タブIDを取得（大タブから）
      const parentSection = panel.closest('section[data-panel]');
      const parentTabId = parentSection ? parentSection.getAttribute('data-panel') : '';
      
      // 地域タブがある場合（region-で始まる）と、地域タブがない場合（大学・小学校）を処理
      let regionTabId = '';
      if (panelId && panelId.startsWith('region-')) {
        regionTabId = panelId;
      } else if (parentTabId === 'tab-university' || parentTabId === 'tab-primary') {
        // 大学と小学校は地域タブがないので、空文字列を渡す
        regionTabId = '';
      } else {
        // その他のパネルはスキップ
        return;
      }

      if (parentTabId) {
        try {
          const html = await fetchSchoolList(parentTabId, regionTabId);
          panel.innerHTML = html;
          panel.setAttribute('data-loaded', 'true');
        } catch (error) {
          console.error('初期表示時のパネル読み込みエラー:', error);
        }
      }
    }
  });

  /**
   * タブを切り替える
   * @param {HTMLElement} tabList - タブリストの要素
   * @param {string} tabId - 表示するタブのID
   */
  async function switchTab(tabList, tabId) {
    const tabs = tabList.querySelectorAll('li[data-tab]');
    
    // 同じタブリスト内のすべてのタブからactiveクラスを削除
    tabs.forEach((tab) => {
      tab.classList.remove('active');
    });

    // 対応するパネルを探す（タブリストの親要素内で検索）
    const container = tabList.closest('.innerOverall, .section, .inner, section, div');
    let panels = [];
    let activePanel = null;
    
    if (container) {
      // コンテナ内のパネルを検索
      panels = container.querySelectorAll('.panel[data-panel], div.panel[data-panel]');
      // コンテナ内で対応するパネルを検索
      activePanel = container.querySelector(`.panel[data-panel="${tabId}"], div.panel[data-panel="${tabId}"]`);
    } else {
      // コンテナが見つからない場合は全体から検索
      panels = document.querySelectorAll('.panel[data-panel], div.panel[data-panel]');
      activePanel = document.querySelector(`.panel[data-panel="${tabId}"], div.panel[data-panel="${tabId}"]`);
    }

    // すべてのパネルからshowクラスを削除
    panels.forEach((panel) => {
      panel.classList.remove('show');
    });

    // クリックされたタブにactiveクラスを追加
    const activeTab = tabList.querySelector(`li[data-tab="${tabId}"]`);
    if (activeTab) {
      activeTab.classList.add('active');
    }

    // 対応するパネルにshowクラスを追加
    if (activePanel) {
      activePanel.classList.add('show');

      // 学校リストのタブ（region-で始まる）のみAJAXでデータを取得
      const isSchoolListTab = tabId.startsWith('region-');
      
      if (isSchoolListTab) {
        // パネルが未読み込みの場合はAJAXでデータを取得
        const isLoaded = activePanel.getAttribute('data-loaded') === 'true';
        if (!isLoaded) {
          // ローディング表示
          activePanel.innerHTML = '<ul class="schoolList"><li>読み込み中...</li></ul>';

          // 親タブIDを取得（大タブから）
          const parentSection = activePanel.closest('section[data-panel]');
          const parentTabId = parentSection ? parentSection.getAttribute('data-panel') : '';

          // 地域タブIDは現在のtabId
          const regionTabId = tabId;

          if (parentTabId && regionTabId) {
            try {
              const html = await fetchSchoolList(parentTabId, regionTabId);
              activePanel.innerHTML = html;
              activePanel.setAttribute('data-loaded', 'true');
            } catch (error) {
              console.error('タブデータの読み込みエラー:', error);
              activePanel.innerHTML = '<ul class="schoolList"><li>データの読み込みに失敗しました。</li></ul>';
            }
          }
        }
      } else if (tabId === 'tab-university' || tabId === 'tab-primary') {
        // 地域タブがない場合（大学・小学校）をクリックした際
        // 直接のパネルを確認し、未読み込みの場合はAJAXで取得
        // 少し遅延を入れて、DOMの更新を待つ
        setTimeout(async () => {
          const directPanel = activePanel.querySelector('.panel.show[data-loaded="false"]');
          if (directPanel) {
            const isLoaded = directPanel.getAttribute('data-loaded') === 'true';
            if (!isLoaded) {
              // ローディング表示
              directPanel.innerHTML = '<ul class="schoolList"><li>読み込み中...</li></ul>';

              // 親タブIDは現在のtabId（大タブ）
              const parentTabId = tabId;
              const regionTabId = '';

              if (parentTabId) {
                try {
                  const html = await fetchSchoolList(parentTabId, regionTabId);
                  directPanel.innerHTML = html;
                  directPanel.setAttribute('data-loaded', 'true');
                } catch (error) {
                  console.error('タブデータの読み込みエラー:', error);
                  directPanel.innerHTML = '<ul class="schoolList"><li>データの読み込みに失敗しました。</li></ul>';
                }
              }
            }
          }
        }, 0);
      } else if (tabId.startsWith('tab-')) {
        // style01のタブ（tab-language, tab-specialty, tab-high-school）をクリックした際
        // その中にある最初のactiveな地域タブのパネルを確認し、未読み込みの場合はAJAXで取得
        // 少し遅延を入れて、DOMの更新を待つ
        setTimeout(async () => {
          // 地域タブがある場合（tab-language, tab-specialty, tab-high-school）
          const firstRegionTab = activePanel.querySelector('ul.tab.style02 li.active[data-tab]');
          if (firstRegionTab) {
            const firstRegionTabId = firstRegionTab.getAttribute('data-tab');
            if (firstRegionTabId && firstRegionTabId.startsWith('region-')) {
              // 大タブ内の全ての地域タブパネルからshowクラスを削除
              const allRegionPanels = activePanel.querySelectorAll('.panel[data-panel^="region-"]');
              allRegionPanels.forEach((panel) => {
                panel.classList.remove('show');
              });

              const firstRegionPanel = activePanel.querySelector(`.panel[data-panel="${firstRegionTabId}"]`);
              if (firstRegionPanel) {
                // 最初の地域タブのパネルにshowクラスを追加
                firstRegionPanel.classList.add('show');

                const isLoaded = firstRegionPanel.getAttribute('data-loaded') === 'true';
                // data-loaded属性がない、またはfalseの場合はAJAXで取得
                if (!isLoaded) {
                  // ローディング表示
                  firstRegionPanel.innerHTML = '<ul class="schoolList"><li>読み込み中...</li></ul>';

                  // 親タブIDは現在のtabId（大タブ）
                  const parentTabId = tabId;
                  const regionTabId = firstRegionTabId;

                  if (parentTabId && regionTabId) {
                    try {
                      const html = await fetchSchoolList(parentTabId, regionTabId);
                      firstRegionPanel.innerHTML = html;
                      firstRegionPanel.setAttribute('data-loaded', 'true');
                    } catch (error) {
                      console.error('タブデータの読み込みエラー:', error);
                      firstRegionPanel.innerHTML = '<ul class="schoolList"><li>データの読み込みに失敗しました。</li></ul>';
                    }
                  }
                }
              }
            }
          }
        }, 0);
      }
    }
  }

  // 各タブリストに対して処理を実行
  tabLists.forEach((tabList) => {
    const tabs = tabList.querySelectorAll('li[data-tab]');
    
    if (tabs.length === 0) {
      return;
    }

    // 各タブにクリックイベントを設定
    tabs.forEach((tab) => {
      tab.addEventListener('click', function () {
        const tabId = this.getAttribute('data-tab');
        if (tabId) {
          switchTab(tabList, tabId);
        }
      });
    });
  });
}

